import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { getFocusRingStyle, updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowFab: Components<Theme>['MuiFab'] = {
  defaultProps: {
    color: 'primary',
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      transition:
        'transform 0.3s ease-out, color 0.3s ease-out, background-color 0.3s ease-out, border-color 0.3s ease-out, box-shadow 0.3s ease-out, opacity 0.3s ease-out',
      // by default, fab contains shadow
      '&, &:active, &.Mui-disabled, &.Mui-focusVisible': {
        boxShadow: ownerState.raised === false ? 'none' : theme.flowShadows.xs,
      },
      outline: 'none',
      flexShrink: '0',
      // 50% border radius without skewing ratio
      borderRadius: '1000px',
      // 44px
      padding: '11px',
      height: 'auto',
      width: 'auto',
      minWidth: '0',
      minHeight: '0',
      // space between icon and text
      gap: '10px',
      // label
      textRendering: 'geometricPrecision',
      ...theme.typography.button,
      // icon itself
      '& .MuiSvgIcon-root': {
        fontSize: '20px',
      },
      // focused style
      ...getFocusRingStyle(theme, true, 1),
      // default (secondary variant)
      border: `1px solid ${theme.palette.onSurface.os500}`,
      ...getInteractiveStyle(
        {
          backgroundColor: theme.palette.onSurface.os0,
          color: theme.palette.onSurface.os900,
        },
        {
          backgroundColor: theme.palette.surface.secondary,
          color: theme.palette.onSurface.os900,
        },
      ),
      // disabled style
      '&.Mui-disabled': {
        backgroundColor: theme.palette.surface.secondary,
        borderColor: theme.palette.surface.secondary,
        color: theme.palette.disabled.primary,
      },
    }),
    // fab with label
    extended: {
      // adjust proportions
      paddingLeft: '21px',
      paddingRight: '19px',
    },
    primary: ({ theme, ownerState }) => ({
      // remove border
      border: 'none',
      // update padding without changing proportions
      padding: '12px',
      ...(ownerState.variant === 'extended' && {
        paddingLeft: '22px',
        paddingRight: '20px',
      }),
      // and focus ring
      ...updateFocusRingStyle(0),
      // style
      ...getInteractiveStyle(
        {
          backgroundColor: theme.palette.brand.secondary,
          color: theme.palette.onSurface.os900,
        },
        {
          backgroundColor: theme.palette.brandHover.secondary,
          color: theme.palette.onSurface.os900,
        },
      ),
      // disabled style
      '&.Mui-disabled': {
        backgroundColor: theme.palette.disabled.secondary,
        color: theme.palette.disabled.tertiary,
      },
    }),
  },
  variants: [
    {
      props: { expandable: true },
      style: {
        paddingLeft: '11px',
        paddingRight: '11px',
        // hide label wrapped in span
        '& > span': {
          display: 'none',
        },
        // on hover/focus
        '&:hover, &.Mui-focusVisible': {
          paddingLeft: '21px',
          paddingRight: '19px',
          // show label
          '& > span': {
            display: 'inline',
          },
        },
      },
    },
    {
      props: { expandable: true, color: 'primary' },
      style: {
        paddingLeft: '12px',
        paddingRight: '12px',
        // on hover/focus
        '&:hover, &.Mui-focusVisible': {
          // change to extended variant
          paddingLeft: '22px',
          paddingRight: '20px',
        },
      },
    },
  ],
};
